import React, { useState, useEffect } from "react";
import {
    Table,
    Button,
    Space,
    message,
    Popconfirm,
    Card,
    Image,
    Statistic,
    Typography,
    List,
    Tag,
    Badge,
    Row,
    Col,
    Input,
    Select,
    Alert,
    Empty,
    Tooltip,
} from "antd";
import {
    EyeOutlined,
    CheckCircleOutlined,
    ClockCircleOutlined,
    FileTextOutlined,
    TrophyOutlined,
    SearchOutlined,
    FilterOutlined,
    PushpinOutlined,
    PushpinFilled,
    PlusOutlined,
    EditOutlined,
    DeleteOutlined,
    FileProtectOutlined,
} from "@ant-design/icons";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import Cookies from "universal-cookie";
import { useLocalStorage } from "../hooks/useLocalStorage";

const { Title, Paragraph, Text } = Typography;
const { Option } = Select;
const { Search } = Input;

const CourseList = () => {
    const [courses, setCourses] = useState([]);
    const [userCourses, setUserCourses] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filter, setFilter] = useState("all");
    const [searchText, setSearchText] = useState("");
    const [pinningCourse, setPinningCourse] = useState(null);
    const navigate = useNavigate();
    const cookies = new Cookies();
    const auth = cookies.get("authToken");
    const config = { headers: { "auth-token": auth } };
    const { userData, updateUserData } = useLocalStorage();

    useEffect(() => {
        fetchCourses();
    }, [pinningCourse]);

    const fetchCourses = async () => {
        setLoading(true);
        try {
            if (userData.role === "student") {
                // const response = await axios.get("/api/courses", config);
                // setCourses(response.data);

                // เรียก API ทั้งหมดพร้อมกัน
                const [coursesResponse, userCoursesResponse] = await Promise.all([
                    // ดึงข้อมูลคอร์สทั้งหมด
                    axios.get("/api/courses", config),
                    // ดึงข้อมูลคอร์สที่ผู้ใช้ลงทะเบียน
                    axios.get("/api/user-courses", config),
                ]);

                const c = coursesResponse.data;
                const uc = userCoursesResponse.data;
                const c_filtered = c.filter(
                    (course) =>
                        !uc
                            .filter((uc) => uc.status === "passed")
                            .map((uc) => uc.course_id)
                            .includes(course.id),
                );
                setCourses(c_filtered);

                // setCourses(coursesResponse.data);
                setUserCourses(userCoursesResponse.data);
                setLoading(false);
            } else {
                const response = await axios.get(`/api/courses/teacher`, config);
                setCourses(response.data);
                setLoading(false);
            }
        } catch (error) {
            message.error("Failed to fetch courses");
        }
    };

    // กรองรายการคอร์สตามสถานะ
    const getFilteredCourses = () => {
        // กรองตามข้อความค้นหา
        let filtered = courses;

        // filtered = filtered.filter(
        //     (course) =>
        //         !userCourses
        //             .filter((uc) => uc.status === "passed")
        //             .map((uc) => uc.course_id)
        //             .includes(course.id)
        // );

        if (searchText) {
            filtered = filtered.filter(
                (course) =>
                    course.title.toLowerCase().includes(searchText.toLowerCase()) ||
                    (course.description && course.description.toLowerCase().includes(searchText.toLowerCase())) ||
                    (course.category && course.category.toLowerCase().includes(searchText.toLowerCase())),
            );
        }

        // กรองตามสถานะการลงทะเบียน
        if (filter === "enrolled") {
            // คอร์สที่ลงทะเบียนแล้ว
            const enrolledCourseIds = userCourses.map((uc) => uc.course_id);
            filtered = filtered.filter((course) => enrolledCourseIds.includes(course.id));
        } else if (filter === "completed") {
            // คอร์สที่เรียนจบแล้ว
            const completedCourseIds = userCourses
                .filter((uc) => uc.status === "completed" || uc.status === "passed")
                .map((uc) => uc.course_id);
            filtered = filtered.filter((course) => completedCourseIds.includes(course.id));
        } else if (filter === "passed") {
            // คอร์สที่สอบผ่านแล้ว
            const passedCourseIds = userCourses.filter((uc) => uc.status === "passed").map((uc) => uc.course_id);
            filtered = filtered.filter((course) => passedCourseIds.includes(course.id));
        } else if (filter === "in_progress") {
            // คอร์สที่กำลังเรียน
            const inProgressCourseIds = userCourses
                .filter((uc) => uc.status === "in_progress")
                .map((uc) => uc.course_id);
            filtered = filtered.filter((course) => inProgressCourseIds.includes(course.id));
        } else if (filter === "pinned") {
            // คอร์สที่ปักหมุดไว้
            const pinnedCourseIds = userCourses.filter((uc) => uc.is_pinned === 1).map((uc) => uc.course_id);
            filtered = filtered.filter((course) => pinnedCourseIds.includes(course.id));
        }

        // เรียงลำดับโดยนำคอร์สที่ปักหมุดขึ้นก่อน
        filtered = [...filtered].sort((a, b) => {
            const aIsPinned = userCourses.find((uc) => uc.course_id === a.id && uc.is_pinned === 1) ? 1 : 0;
            const bIsPinned = userCourses.find((uc) => uc.course_id === b.id && uc.is_pinned === 1) ? 1 : 0;

            // เรียงตามปักหมุด (ลดลง) ถ้าเท่ากันให้เรียงตาม id (เพิ่มขึ้น)
            return bIsPinned - aIsPinned || a.id - b.id;
        });

        return filtered;
    };

    // หาข้อมูลการลงทะเบียนของผู้ใช้สำหรับคอร์สที่ระบุ
    const getUserCourseStatus = (courseId) => {
        const userCourse = userCourses.find((uc) => uc.course_id === courseId);
        return userCourse ? userCourse.status : null;
    };

    const handleDelete = async (id) => {
        try {
            await axios.delete(`/api/courses/${id}`, config);
            message.success("Course deleted successfully");
            fetchCourses();
        } catch (error) {
            message.error("Failed to delete course");
        }
    };

    // ฟังก์ชันสำหรับปักหมุด/ยกเลิกการปักหมุดคอร์ส
    const togglePinCourse = async (courseId, currentPinStatus) => {
        try {
            setPinningCourse(courseId); // เริ่มปักหมุด - แสดง loading

            const response = await axios.put(
                `/api/user-courses/${courseId}/pin`,
                { isPinned: !currentPinStatus },
                config,
            );

            // อัปเดต state ของ userCourses
            setUserCourses((prevCourses) => {
                // หา index ของคอร์สที่อัปเดต
                const index = prevCourses.findIndex((c) => c.course_id === courseId);

                if (index !== -1) {
                    // อัปเดตคอร์สที่มีอยู่แล้ว
                    const updatedCourses = [...prevCourses];
                    updatedCourses[index] = {
                        ...updatedCourses[index],
                        is_pinned: !currentPinStatus,
                    };
                    return updatedCourses;
                } else {
                    // เพิ่มคอร์สใหม่ (กรณีไม่เคยลงทะเบียน)
                    return [
                        ...prevCourses,
                        {
                            ...response.data.enrollment,
                            course_id: courseId,
                            is_pinned: !currentPinStatus,
                        },
                    ];
                }
            });

            message.success(currentPinStatus ? "Unpinned course successfully" : "Pinned course successfully");
        } catch (error) {
            console.error("Error toggling pin status:", error);
            message.error("Failed to update pin status");
        } finally {
            setPinningCourse(null); // เสร็จสิ้นการปักหมุด
        }
    };

    // ตรวจสอบว่าคอร์สถูกปักหมุดหรือไม่
    const isCoursePinned = (courseId) => {
        const userCourse = userCourses.find((uc) => uc.course_id === courseId);
        return userCourse ? userCourse.is_pinned === 1 : false;
    };

    // const columns = [
    //     {
    //         title: " ",
    //         dataIndex: "image_path",
    //         key: "image_path",
    //         render: (image_path, record) => (
    //             <a href={`/course/${record.id}`}>
    //                 <Image
    //                     src={image_path}
    //                     alt="รูปภาพสินค้า"
    //                     width={80}
    //                     height={60}
    //                     style={{ objectFit: "cover" }}
    //                     preview={false}
    //                     fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMIAAADDCAYAAADQvc6UAAABRWlDQ1BJQ0MgUHJvZmlsZQAAKJFjYGASSSwoyGFhYGDIzSspCnJ3UoiIjFJgf8LAwSDCIMogwMCcmFxc4BgQ4ANUwgCjUcG3awyMIPqyLsis7PPOq3QdDFcvjV3jOD1boQVTPQrgSkktTgbSf4A4LbmgqISBgTEFyFYuLykAsTuAbJEioKOA7DkgdjqEvQHEToKwj4DVhAQ5A9k3gGyB5IxEoBmML4BsnSQk8XQkNtReEOBxcfXxUQg1Mjc0dyHgXNJBSWpFCYh2zi+oLMpMzyhRcASGUqqCZ16yno6CkYGRAQMDKMwhqj/fAIcloxgHQqxAjIHBEugw5sUIsSQpBobtQPdLciLEVJYzMPBHMDBsayhILEqEO4DxG0txmrERhM29nYGBddr//5/DGRjYNRkY/l7////39v///y4Dmn+LgeHANwDrkl1AuO+pmgAAADhlWElmTU0AKgAAAAgAAYdpAAQAAAABAAAAGgAAAAAAAqACAAQAAAABAAAAwqADAAQAAAABAAAAwwAAAAD9b/HnAAAHlklEQVR4Ae3dP3PTWBSGcbGzM6GCKqlIBRV0dHRJFarQ0eUT8LH4BnRU0NHR0UEFVdIlFRV7TzRksomPY8uykTk/zewQfKw/9znv4yvJynLv4uLiV2dBoDiBf4qP3/ARuCRABEFAoBEgghggQAQZQKAnYEaQBAQaASKIAQJEkAEEegJmBElAoBEgghggQAQZQKAnYEaQBAQaASKIAQJEkAEEegJmBElAoBEgghggQAQZQKAnYEaQBAQaASKIAQJEkAEEegJmBElAoBEgghggQAQZQKAnYEaQBAQaASKIAQJEkAEEegJmBElAoBEgghggQAQZQKAnYEaQBAQaASKIAQJEkAEEegJmBElAoBEgghggQAQZQKAnYEaQBAQaASKIAQJEkAEEegJmBElAoBEgghggQAQZQKAnYEaQBAQaASKIAQJEkAEEegJmBElAoBEgghggQAQZQKAnYEaQBAQaASKIAQJEkAEEegJmBElAoBEgghggQAQZQKAnYEaQBAQaASKIAQJEkAEEegJmBElAoBEgghggQAQZQKAnYEaQBAQaASKIAQJEkAEEegJmBElAoBEgghggQAQZQKAnYEaQBAQaASKIAQJEkAEEegJmBElAoBEgghggQAQZQKAnYEaQBAQaASKIAQJEkAEEegJmBElAoBEgghgg0Aj8i0JO4OzsrPv69Wv+hi2qPHr0qNvf39+iI97soRIh4f3z58/u7du3SXX7Xt7Z2enevHmzfQe+oSN2apSAPj09TSrb+XKI/f379+08+A0cNRE2ANkupk+ACNPvkSPcAAEibACyXUyfABGm3yNHuAECRNgAZLuYPgEirKlHu7u7XdyytGwHAd8jjNyng4OD7vnz51dbPT8/7z58+NB9+/bt6jU/TI+AGWHEnrx48eJ/EsSmHzx40L18+fLyzxF3ZVMjEyDCiEDjMYZZS5wiPXnyZFbJaxMhQIQRGzHvWR7XCyOCXsOmiDAi1HmPMMQjDpbpEiDCiL358eNHurW/5SnWdIBbXiDCiA38/Pnzrce2YyZ4//59F3ePLNMl4PbpiL2J0L979+7yDtHDhw8vtzzvdGnEXdvUigSIsCLAWavHp/+qM0BcXMd/q25n1vF57TYBp0a3mUzilePj4+7k5KSLb6gt6ydAhPUzXnoPR0dHl79WGTNCfBnn1uvSCJdegQhLI1vvCk+fPu2ePXt2tZOYEV6/fn31dz+shwAR1sP1cqvLntbEN9MxA9xcYjsxS1jWR4AIa2Ibzx0tc44fYX/16lV6NDFLXH+YL32jwiACRBiEbf5KcXoTIsQSpzXx4N28Ja4BQoK7rgXiydbHjx/P25TaQAJEGAguWy0+2Q8PD6/Ki4R8EVl+bzBOnZY95fq9rj9zAkTI2SxdidBHqG9+skdw43borCXO/ZcJdraPWdv22uIEiLA4q7nvvCug8WTqzQveOH26fodo7g6uFe/a17W3+nFBAkRYENRdb1vkkz1CH9cPsVy/jrhr27PqMYvENYNlHAIesRiBYwRy0V+8iXP8+/fvX11Mr7L7ECueb/r48eMqm7FuI2BGWDEG8cm+7G3NEOfmdcTQw4h9/55lhm7DekRYKQPZF2ArbXTAyu4kDYB2YxUzwg0gi/41ztHnfQG26HbGel/crVrm7tNY+/1btkOEAZ2M05r4FB7r9GbAIdxaZYrHdOsgJ/wCEQY0J74TmOKnbxxT9n3FgGGWWsVdowHtjt9Nnvf7yQM2aZU/TIAIAxrw6dOnAWtZZcoEnBpNuTuObWMEiLAx1HY0ZQJEmHJ3HNvGCBBhY6jtaMoEiJB0Z29vL6ls58vxPcO8/zfrdo5qvKO+d3Fx8Wu8zf1dW4p/cPzLly/dtv9Ts/EbcvGAHhHyfBIhZ6NSiIBTo0LNNtScABFyNiqFCBChULMNNSdAhJyNSiECRCjUbEPNCRAhZ6NSiAARCjXbUHMCRMjZqBQiQIRCzTbUnAARcjYqhQgQoVCzDTUnQIScjUohAkQo1GxDzQkQIWejUogAEQo121BzAkTI2agUIkCEQs021JwAEXI2KoUIEKFQsw01J0CEnI1KIQJEKNRsQ80JECFno1KIABEKNdtQcwJEyNmoFCJAhELNNtScABFyNiqFCBChULMNNSdAhJyNSiECRCjUbEPNCRAhZ6NSiAARCjXbUHMCRMjZqBQiQIRCzTbUnAARcjYqhQgQoVCzDTUnQIScjUohAkQo1GxDzQkQIWejUogAEQo121BzAkTI2agUIkCEQs021JwAEXI2KoUIEKFQsw01J0CEnI1KIQJEKNRsQ80JECFno1KIABEKNdtQcwJEyNmoFCJAhELNNtScABFyNiqFCBChULMNNSdAhJyNSiECRCjUbEPNCRAhZ6NSiAARCjXbUHMCRMjZqBQiQIRCzTbUnAARcjYqhQgQoVCzDTUnQIScjUohAkQo1GxDzQkQIWejUogAEQo121BzAkTI2agUIkCEQs021JwAEXI2KoUIEKFQsw01J0CEnI1KIQJEKNRsQ80JECFno1KIABEKNdtQcwJEyNmoFCJAhELNNtScABFyNiqFCBChULMNNSdAhJyNSiEC/wGgKKC4YMA4TAAAAABJRU5ErkJggg=="
    //                 />
    //             </a>
    //         ),
    //     },
    //     {
    //         title: "Title",
    //         dataIndex: "title",
    //         key: "title",
    //     },
    //     {
    //         title: "Category",
    //         dataIndex: "category",
    //         key: "category",
    //     },
    //     {
    //         title: "Status",
    //         dataIndex: "status",
    //         key: "status",
    //     },
    //     {
    //         title: "View",
    //         dataIndex: "view_count",
    //         key: "view_count",
    //         render: (view_count, record) => (
    //             <Statistic value={view_count || 0} prefix={<EyeOutlined />} style={{ marginLeft: "20px" }} />
    //         ),
    //     },
    //     {
    //         title: "Actions",
    //         key: "actions",
    //         render: (_, record) => (
    //             <Space>
    //                 <Button type="primary" onClick={() => navigate(`/course/${record.id}`)}>
    //                     {userData && userData.role !== "student" ? "ดูบทเรียน" : "เริ่มเรียน"}
    //                 </Button>
    //                 {userData && userData.role !== "student" && (
    //                     <>
    //                         <Button color="blue" variant="solid" onClick={() => navigate(`/course/edit/${record.id}`)}>
    //                             แก้ไข ​(บทเรียน/ข้อสอบ)
    //                         </Button>
    //                         <Popconfirm
    //                             title="ต้องการลบบทเรียนนี้ใช่หรือไม่?"
    //                             // title="Are you sure you want to delete this course?"
    //                             onConfirm={() => handleDelete(record.id)}
    //                             okText="ยืนยัน"
    //                             cancelText="ยกเลิก"
    //                         >
    //                             <Button danger>ลบ</Button>
    //                         </Popconfirm>
    //                     </>
    //                 )}
    //             </Space>
    //         ),
    //     },
    // ];

    const filteredCourses = getFilteredCourses();
    const pinnedCoursesCount = userCourses.filter((uc) => uc.is_pinned === 1).length;

    return (
        <Card>
            {userData && userData.role !== "student" && (
                <Button type="primary" onClick={() => navigate("/course/new")} style={{ marginBottom: 16 }}>
                    <PlusOutlined />
                    {` สร้างใบประกาศ`}
                </Button>
            )}

            <Row gutter={[16, 16]} align="middle" justify="space-between" style={{ marginBottom: "20px" }}>
                <Col>
                    <Title level={3}>ใบประกาศทั้งหมด</Title>
                </Col>
                <Col xs={24} sm={24} md={12} lg={8}>
                    <Search
                        placeholder="Search courses..."
                        allowClear
                        enterButton={<SearchOutlined />}
                        size="large"
                        onSearch={(value) => setSearchText(value)}
                        onChange={(e) => setSearchText(e.target.value)}
                    />
                </Col>
            </Row>

            <Row style={{ marginBottom: "20px", marginTop: "10px" }}>
                <Col>
                    <Space align="center">
                        <FilterOutlined />
                        <Text strong>Filter:</Text>
                        <Select defaultValue="all" style={{ width: 150 }} onChange={(value) => setFilter(value)}>
                            <Option value="all">All Courses</Option>
                            <Option value="pinned">Pinned ({pinnedCoursesCount})</Option>
                            {/* {userData && userData.role === "student" && <Option value="enrolled">Enrolled</Option>} */}
                            {/* <Option value="in_progress">In Progress</Option> */}
                            {/* <Option value="completed">Completed</Option> */}
                            {/* <Option value="passed">Passed</Option> */}
                        </Select>
                    </Space>
                </Col>
            </Row>

            {userCourses.length > 0 && filter === "all" && (
                <Alert
                    message="Your Learning Progress"
                    description={
                        <div>
                            <Text>You are enrolled in {userCourses.length} courses.</Text>
                            <Text style={{ marginLeft: "10px" }}>
                                Qualified: {userCourses.filter((uc) => uc.status === "passed").length}, In Progress:{" "}
                                {userCourses.filter((uc) => uc.status === "in_progress").length}
                            </Text>
                        </div>
                    }
                    type="info"
                    showIcon
                    style={{ marginBottom: "20px" }}
                />
            )}

            {filteredCourses.length === 0 ? (
                <Empty
                    description={
                        searchText
                            ? `No courses match "${searchText}"`
                            : filter !== "all"
                              ? `No courses in "${filter}" category`
                              : "ไม่มีใบประกาศที่พร้อมให้เรียนในขณะนี้"
                    }
                />
            ) : (
                <List
                    grid={{
                        gutter: 16,
                        xs: 1,
                        sm: 2,
                        md: 3,
                        lg: 3,
                        xl: 4,
                        xxl: 5,
                    }}
                    dataSource={filteredCourses}
                    renderItem={(course) => {
                        const userCourseStatus = getUserCourseStatus(course.id);
                        const isPassed = userCourseStatus === "passed";
                        const isPinned = isCoursePinned(course.id);

                        return (
                            <List.Item>
                                {isPassed ? (
                                    <Badge.Ribbon
                                        text={
                                            <Space>
                                                <FileProtectOutlined /> Qualified
                                            </Space>
                                        }
                                        color="gold"
                                        style={{ marginTop: "40px" }}
                                    >
                                        <CourseCard
                                            course={course}
                                            userCourseStatus={userCourseStatus}
                                            userData={userData}
                                            handleDelete={handleDelete}
                                            isPinned={isPinned}
                                            onTogglePin={() => togglePinCourse(course.id, isPinned)}
                                            isPinning={pinningCourse === course.id}
                                        />
                                    </Badge.Ribbon>
                                ) : (
                                    <Badge.Ribbon
                                        text={
                                            <Space>
                                                <EyeOutlined /> {course.view_count || 0}
                                            </Space>
                                        }
                                        color="blue"
                                        style={{ marginTop: "40px" }}
                                    >
                                        <CourseCard
                                            course={course}
                                            userCourseStatus={userCourseStatus}
                                            userData={userData}
                                            handleDelete={handleDelete}
                                            isPinned={isPinned}
                                            onTogglePin={() => togglePinCourse(course.id, isPinned)}
                                            isPinning={pinningCourse === course.id}
                                        />
                                    </Badge.Ribbon>
                                )}
                            </List.Item>
                        );
                    }}
                />
            )}

            {/* <Table columns={columns} dataSource={courses} rowKey="id" loading={loading} /> */}
        </Card>
    );
};

// แยกส่วน Card ออกมาเพื่อให้โค้ดอ่านง่ายขึ้น
const CourseCard = ({ course, userCourseStatus, userData, handleDelete, isPinned, onTogglePin, isPinning }) => {
    const navigate = useNavigate();

    // แสดง Tag สำหรับสถานะคอร์ส
    const renderStatusTag = (status) => {
        if (!status) return null;

        switch (status) {
            case "enrolled":
                return (
                    <Tag icon={<ClockCircleOutlined />} color="blue">
                        Enrolled
                    </Tag>
                );
            case "in_progress":
                return (
                    <Tag icon={<FileTextOutlined />} color="processing">
                        In Progress
                    </Tag>
                );
            case "completed":
                return (
                    <Tag icon={<CheckCircleOutlined />} color="success">
                        Completed
                    </Tag>
                );
            case "passed":
                return (
                    <Tag icon={<FileProtectOutlined />} color="gold">
                        Qualified <CheckCircleOutlined style={{ color: "#52c41a" }} />
                    </Tag>
                );
            case "failed":
                return <Tag color="error">Failed</Tag>;
            default:
                return null;
        }
    };

    return (
        <Card
            hoverable
            style={{
                height: "100%", // กำหนดความสูง 100% ของพื้นที่ที่มี
                display: "flex",
                flexDirection: "column",
            }}
            cover={
                <div style={{ height: "200px", overflow: "hidden" }}>
                    {course.image_path ? (
                        <img
                            alt={course.title}
                            src={course.image_path}
                            style={{ width: "100%", height: "100%", objectFit: "cover" }}
                        />
                    ) : (
                        <div
                            style={{
                                height: "100%",
                                width: "100%",
                                background: "#f0f2f5",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        >
                            <Typography.Text disabled>No Image</Typography.Text>
                        </div>
                    )}
                    <Tooltip title={isPinned ? "Unpin course" : "Pin course"}>
                        <Button
                            type="text"
                            size="small"
                            icon={isPinned ? <PushpinFilled /> : <PushpinOutlined />}
                            onClick={(e) => {
                                e.preventDefault(); // ป้องกันการนำทางเมื่อคลิกที่ปุ่ม
                                onTogglePin();
                            }}
                            loading={isPinning}
                            style={{
                                position: "absolute",
                                top: "8px",
                                right: "8px",
                                background: isPinned ? "rgba(255, 255, 255, 0.9)" : "rgba(255, 255, 255, 0.7)",
                                color: isPinned ? "#1890ff" : "rgba(0, 0, 0, 0.65)",
                                borderRadius: "50%",
                                width: "32px",
                                height: "32px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                boxShadow: "0 2px 6px rgba(0, 0, 0, 0.15)",
                                zIndex: 1,
                            }}
                        />
                    </Tooltip>
                </div>
            }
            actions={[
                userData && userData.role !== "student" ? (
                    <Space>
                        {userData && userData.role !== "student" && (
                            <>
                                <Button
                                    color="blue"
                                    variant="solid"
                                    onClick={() => navigate(`/course/edit/${course.id}`)}
                                >
                                    <EditOutlined />
                                    แก้ไข
                                </Button>
                                <Popconfirm
                                    title="ต้องการลบบทเรียนนี้ใช่หรือไม่?"
                                    // title="Are you sure you want to delete this course?"
                                    onConfirm={() => handleDelete(course.id)}
                                    okText="ยืนยัน"
                                    cancelText="ยกเลิก"
                                >
                                    <Button danger>
                                        <DeleteOutlined />
                                        ลบ
                                    </Button>
                                </Popconfirm>
                            </>
                        )}
                    </Space>
                ) : null,
            ].filter(Boolean)}
            bodyStyle={{
                flex: "1 1 auto", // ทำให้เนื้อหาขยายตามพื้นที่ที่เหลือ
                display: "flex",
                flexDirection: "column",
            }}
        >
            <Link to={`/course/${course.id}`} style={{ textDecoration: "none" }}>
                <div style={{ flex: "1 1 auto", marginBottom: "20px" }}>
                    <Card.Meta
                        title={
                            <div
                                style={{
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    marginBottom: "8px",
                                }}
                            >
                                {course.title}
                            </div>
                        }
                        description={
                            <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
                                <div style={{ marginBottom: "10px" }}>
                                    <Space size={[0, 4]} wrap>
                                        {course.category && <Tag color="green">{course.category}</Tag>}
                                        {course.status && (
                                            <Tag color={course.status === "publish" ? "blue" : "red"}>
                                                {course.status}
                                            </Tag>
                                        )}
                                        {renderStatusTag(userCourseStatus)}
                                    </Space>
                                </div>
                                <Paragraph
                                    ellipsis={{ rows: 2 }}
                                    style={{
                                        margin: 0,
                                        flex: "1 1 auto",
                                        minHeight: "3em", // กำหนดความสูงขั้นต่ำให้กับคำอธิบาย
                                    }}
                                >
                                    {course.excerpt || "No excerpt available"}
                                </Paragraph>
                            </div>
                        }
                    />
                </div>
            </Link>
        </Card>
    );
};

export default CourseList;
